/**
 * CategoryDetailSheet [T84P2]
 *
 * Right-side drawer showing rich stats for a single category:
 *  - Hero header with colored background, emoji/initial, name, type badge
 *  - This-month + all-time stats (3-column grid)
 *  - 6-month spending AreaChart using category color
 *  - Recent transactions (last 5, compact list)
 *  - Sticky footer: Filter | Edit | Add Transaction
 */

import { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from 'recharts';
import { Filter, Pencil, Plus, TrendingUp, TrendingDown, ArrowLeftRight } from 'lucide-react';
import { format } from 'date-fns';
import { useCurrency } from '@/hooks/useCurrency';
import api, { endpoints } from '@/services/api';
import type { Category } from '@/lib/types';
import type { CategoryStats } from '@/lib/types';

// ─── Props ─────────────────────────────────────────────────────────────────────

interface CategoryDetailSheetProps {
  categoryId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit?: (category: Category) => void;
  onFilterByCategory?: (categoryId: string) => void;
  onAddTransaction?: (categoryId: string) => void;
}

// ─── Type icon helper ─────────────────────────────────────────────────────────

function TxTypeIcon({ type }: { type: 'income' | 'expense' | 'transfer' }) {
  if (type === 'income') return <TrendingUp className="h-3.5 w-3.5 text-green-600 shrink-0" />;
  if (type === 'expense') return <TrendingDown className="h-3.5 w-3.5 text-red-600 shrink-0" />;
  return <ArrowLeftRight className="h-3.5 w-3.5 text-blue-600 shrink-0" />;
}

// ─── Component ──────────────────────────────────────────────────────────────────

export default function CategoryDetailSheet({
  categoryId,
  open,
  onOpenChange,
  onEdit,
  onFilterByCategory,
  onAddTransaction,
}: CategoryDetailSheetProps) {
  const { fmt } = useCurrency();
  const [stats, setStats] = useState<CategoryStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!open || !categoryId) {
      setStats(null);
      return;
    }
    setIsLoading(true);
    api
      .get(endpoints.categories.stats(categoryId))
      .then((r) => setStats(r.data?.data ?? null))
      .catch(() => setStats(null))
      .finally(() => setIsLoading(false));
  }, [open, categoryId]);

  const cat = stats?.category ?? null;

  const chartColor = cat?.color ?? 'hsl(var(--chart-1))';

  const gradientId = `catGrad-${categoryId ?? 'x'}`;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[calc(100vw-3rem)] sm:max-w-[420px] flex flex-col p-0 gap-0 overflow-hidden"
      >
        {/* Hero header */}
        <div
          className="px-6 pt-8 pb-6"
          style={{ backgroundColor: cat?.color ? `${cat.color}18` : undefined }}
        >
          <SheetHeader className="space-y-3">
            <SheetDescription className="sr-only">Category details</SheetDescription>
            <div className="flex items-center gap-4">
              {/* 48px color circle */}
              <div
                className="h-12 w-12 rounded-full flex items-center justify-center shrink-0 border-2"
                style={{
                  backgroundColor: cat?.color ? `${cat.color}30` : 'hsl(var(--muted))',
                  borderColor: cat?.color ?? 'transparent',
                }}
              >
                {cat?.emoji ? (
                  <span className="text-xl leading-none">{cat.emoji}</span>
                ) : (
                  <span className="text-lg font-semibold text-foreground">
                    {cat?.name?.[0]?.toUpperCase() ?? '?'}
                  </span>
                )}
              </div>
              <div className="min-w-0">
                <SheetTitle className="text-left leading-tight truncate">
                  {cat?.name ?? (isLoading ? '…' : 'Category')}
                </SheetTitle>
                {cat?.type && (
                  <Badge
                    variant="secondary"
                    className={`mt-1 ${cat.type === 'income' ? 'text-green-700 bg-green-100' : 'text-red-700 bg-red-100'}`}
                  >
                    {cat.type.charAt(0).toUpperCase() + cat.type.slice(1)}
                  </Badge>
                )}
              </div>
            </div>
            {cat?.description && (
              <p className="text-sm text-muted-foreground break-words">{cat.description}</p>
            )}
          </SheetHeader>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          {isLoading && !stats ? (
            <div className="px-6 py-5 space-y-4">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : (
            <div className="px-6 py-5 space-y-5">
              {/* Stats row: 3-column grid */}
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg border px-2 py-3">
                  <p className="text-xs text-muted-foreground mb-0.5">This Month</p>
                  <p className="text-sm font-bold tabular-nums">
                    {fmt(stats?.thisMonth.total ?? 0)}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {stats?.thisMonth.count ?? 0} txns
                  </p>
                </div>
                <div className="rounded-lg border px-2 py-3">
                  <p className="text-xs text-muted-foreground mb-0.5">All Time</p>
                  <p className="text-sm font-bold tabular-nums">
                    {fmt(stats?.allTime.total ?? 0)}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {stats?.allTime.count ?? 0} txns
                  </p>
                </div>
                <div className="rounded-lg border px-2 py-3">
                  <p className="text-xs text-muted-foreground mb-0.5">Transactions</p>
                  <p className="text-sm font-bold tabular-nums">
                    {stats?.allTime.count ?? 0}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">total</p>
                </div>
              </div>

              {/* 6-month AreaChart */}
              {stats && stats.last6Months.length > 0 && (
                <div className="h-[80px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={stats.last6Months}
                      margin={{ top: 4, right: 4, bottom: 0, left: 0 }}
                    >
                      <defs>
                        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={chartColor} stopOpacity={0.35} />
                          <stop offset="95%" stopColor={chartColor} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis
                        dataKey="month"
                        tick={{ fontSize: 9 }}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(v: string) => v.slice(5)}
                      />
                      <YAxis hide />
                      <RechartsTooltip
                        formatter={(value: number) => [fmt(value), 'Total']}
                        labelFormatter={(l: string) => l}
                        contentStyle={{ fontSize: 11, padding: '4px 8px' }}
                      />
                      <Area
                        type="monotone"
                        dataKey="total"
                        stroke={chartColor}
                        strokeWidth={2}
                        fill={`url(#${gradientId})`}
                        dot={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}

              <Separator />

              {/* Recent transactions */}
              {stats && stats.recentTransactions.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                    Recent Transactions
                  </p>
                  {stats.recentTransactions.slice(0, 5).map((txn) => (
                    <div
                      key={txn.id}
                      className="flex items-center justify-between gap-2 py-2 hover:bg-muted/30 rounded-md px-1"
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <TxTypeIcon type={txn.type} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">
                            {txn.emoji ? `${txn.emoji} ` : ''}{txn.description}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {format(new Date(txn.date), 'MMM d, yyyy')}
                          </p>
                        </div>
                      </div>
                      <span
                        className={`text-sm font-semibold tabular-nums shrink-0 ${
                          txn.type === 'income'
                            ? 'text-green-600'
                            : txn.type === 'expense'
                              ? 'text-red-600'
                              : 'text-blue-600'
                        }`}
                      >
                        {txn.type === 'income' ? '+' : txn.type === 'expense' ? '-' : ''}
                        {fmt(Math.abs(txn.amount))}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {stats && stats.recentTransactions.length === 0 && (
                <p className="text-sm text-center text-muted-foreground py-4">
                  No transactions for this category yet.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Sticky footer */}
        <SheetFooter className="border-t px-6 py-4 gap-2 flex-row justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { onFilterByCategory?.(categoryId!); onOpenChange(false); }}
            disabled={!categoryId}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filter
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { if (cat) onEdit?.(cat); onOpenChange(false); }}
              disabled={!cat}
            >
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button
              size="sm"
              onClick={() => { onAddTransaction?.(categoryId!); onOpenChange(false); }}
              disabled={!categoryId}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
